"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var store_1 = require("@ngrx/store");
var skynet_store_utilities_1 = require("@synisys/skynet-store-utilities");
var immutable_1 = require("immutable");
var lodash_1 = require("lodash");
var operators_1 = require("rxjs/operators");
var actions_types_enum_1 = require("../actions-types.enum");
var utilities_1 = require("../utilities");
var constants_1 = require("./constants");
var logging_utilities_1 = require("./logging.utilities");
var logger = logging_utilities_1.createLogger('entity-store-manager');
var EntityStoreManager = /** @class */ (function () {
    function EntityStoreManager(path, keyExtractor, prefix, isEmptyPredicate) {
        if (isEmptyPredicate === void 0) { isEmptyPredicate = utilities_1.isEmptyState; }
        var _this = this;
        this.path = path;
        this.keyExtractor = keyExtractor;
        this.prefix = prefix;
        this.isEmptyPredicate = isEmptyPredicate;
        this.selectAll = lodash_1.memoize(this.loadAll, function (args) { return "" + _this.path + (lodash_1.isNil(_this.prefix) ? '' : '.' + _this.prefix); });
        this.selectOne = lodash_1.memoize(this.loadOne, function (_, key, __) {
            if (immutable_1.Record.isRecord(key)) {
                return key.toSeq().join('.');
            }
            else {
                return "" + _this.path + (lodash_1.isNil(_this.prefix) ? '' : '.' + _this.prefix) + "." + String(key);
            }
        });
    }
    EntityStoreManager.prototype.actionType = function (standardType) {
        return this.path + " " + standardType;
    };
    EntityStoreManager.prototype.createAction = function (standardType, payload, _additionalData) {
        var additionalData = this.assignPrefixIfNeeded(_additionalData);
        switch (standardType) {
            case actions_types_enum_1.StandardActionTypes.LOAD_ALL:
                return {
                    type: this.actionType(standardType),
                    additionalData: additionalData,
                };
            case actions_types_enum_1.StandardActionTypes.UPDATE_MANY: {
                return {
                    type: this.actionType(standardType),
                    items: payload,
                    additionalData: additionalData,
                };
            }
            case actions_types_enum_1.StandardActionTypes.UPDATE_MANY_SUCCESS:
            case actions_types_enum_1.StandardActionTypes.LOAD_MANY_SUCCESS:
                return {
                    type: this.actionType(standardType),
                    items: payload,
                    additionalData: additionalData,
                };
            case actions_types_enum_1.StandardActionTypes.LOAD_ONE:
                return {
                    type: this.actionType(standardType),
                    id: payload,
                    additionalData: additionalData,
                };
            case actions_types_enum_1.StandardActionTypes.CREATE:
            case actions_types_enum_1.StandardActionTypes.UPDATE:
                return {
                    type: this.actionType(standardType),
                    item: payload,
                    additionalData: additionalData,
                };
            case actions_types_enum_1.StandardActionTypes.CREATE_SUCCESS:
            case actions_types_enum_1.StandardActionTypes.UPDATE_SUCCESS:
                return {
                    type: this.actionType(standardType),
                    item: payload,
                    additionalData: additionalData,
                };
            case actions_types_enum_1.StandardActionTypes.DELETE:
            case actions_types_enum_1.StandardActionTypes.DELETE_SUCCESS:
                return {
                    type: this.actionType(standardType),
                    item: payload,
                    additionalData: additionalData,
                };
            case actions_types_enum_1.StandardActionTypes.LOAD_ONE_SUCCESS:
                return {
                    type: this.actionType(standardType),
                    item: payload,
                    additionalData: additionalData,
                };
            case actions_types_enum_1.StandardActionTypes.CREATE_FAIL:
            case actions_types_enum_1.StandardActionTypes.ENTITY_ERROR:
                return {
                    type: this.actionType(standardType),
                    error: payload,
                    additionalData: additionalData,
                };
            default:
                throw new TypeError("unknown action type " + standardType);
        }
    };
    EntityStoreManager.prototype.clearCache = function () {
        this.selectOne.cache.clear();
        this.selectAll.cache.clear();
    };
    EntityStoreManager.prototype.getPathExtended = function () {
        return "" + this.path + (lodash_1.isNil(this.prefix) ? '' : '.' + this.prefix);
    };
    EntityStoreManager.prototype.assignPrefixIfNeeded = function (additionalData) {
        if (this.prefix) {
            return lodash_1.assign(additionalData, { __prefix__: this.prefix });
        }
        return additionalData;
    };
    EntityStoreManager.prototype.loadOne = function (store, id, additionalData) {
        var _this = this;
        store.dispatch(this.createAction(actions_types_enum_1.StandardActionTypes.LOAD_ONE, id, additionalData));
        return store
            .select(function (state) { return lodash_1.get(state, _this.getPathExtended()); })
            .pipe(operators_1.filter(lodash_1.negate(this.isEmptyPredicate)), operators_1.filter(function (items) {
            return items.some(function (item) {
                var key = _this.keyExtractor(item);
                if (immutable_1.Record.isRecord(key)) {
                    return key.equals(id);
                }
                else {
                    return (String(key).toLocaleLowerCase() ===
                        String(id).toLocaleLowerCase());
                }
            });
        }), operators_1.map(function (items) {
            return items.find(function (item) {
                var key = _this.keyExtractor(item);
                if (immutable_1.Record.isRecord(key)) {
                    return key.equals(id);
                }
                else {
                    return (String(key).toLocaleLowerCase() ===
                        String(id).toLocaleLowerCase());
                }
            });
        }), operators_1.distinct());
    };
    EntityStoreManager.prototype.loadAll = function (store, additionalData) {
        var _this = this;
        store.dispatch(this.createAction(actions_types_enum_1.StandardActionTypes.LOAD_ALL, undefined, additionalData));
        logger.debug('loading following store item with following key %s', "" + this.path);
        return this.fullyLoaded(store).pipe(operators_1.switchMap(function () {
            return store.select(function (state) { return lodash_1.get(state, _this.getPathExtended()); });
        }), operators_1.filter(lodash_1.negate(this.isEmptyPredicate)), operators_1.publishReplay(1), operators_1.refCount());
    };
    EntityStoreManager.prototype.fullyLoaded = function (store) {
        var featureName = this.getPathExtended().split('.')[0];
        var loadingStatus = featureName + '.' + constants_1.loadingStatusKey;
        var loadStatusPath = this.getPathExtended()
            .split('.')
            .slice(1)
            .join('.');
        return store.pipe(operators_1.filter(function (state) { return skynet_store_utilities_1.notNil(lodash_1.get(state, featureName)); }), store_1.select(function (state) { return lodash_1.get(state, loadingStatus); }), operators_1.map(function (status) { return lodash_1.isNil(status) || lodash_1.get(status, loadStatusPath); }), operators_1.filter(Boolean), operators_1.first());
    };
    return EntityStoreManager;
}());
exports.EntityStoreManager = EntityStoreManager;
//# sourceMappingURL=entity-store.manager.js.map